import React, { useEffect, useMemo, useState } from "react";
import { api, formatCurrency, formatDate } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BookOpenText, Scales } from "@phosphor-icons/react";

// Module-level caches — CoA and posting rules rarely change within a session.
let coaPromise = null;
let mapPromise = null;

export function useCoA() {
  const [accounts, setAccounts] = useState([]);
  useEffect(() => {
    if (!coaPromise) {
      coaPromise = api.get("/finance/gl/accounts").then(({ data }) => data.items || []).catch(() => []);
    }
    let on = true;
    coaPromise.then((items) => { if (on) setAccounts(items); });
    return () => { on = false; };
  }, []);
  const byCode = useMemo(() => Object.fromEntries(accounts.map((a) => [a.code, a])), [accounts]);
  return { accounts, byCode };
}

export function usePostingMap() {
  const [map, setMap] = useState(null);
  useEffect(() => {
    if (!mapPromise) {
      mapPromise = api.get("/finance/gl/posting-map").then(({ data }) => data).catch(() => null);
    }
    let on = true;
    mapPromise.then((m) => { if (on) setMap(m); });
    return () => { on = false; };
  }, []);
  return map;
}

// Chart-of-Accounts dropdown, filtered by account type(s). Value = account code.
export function GLAccountSelect({ value, onChange, types, placeholder = "Select GL account…", testid }) {
  const { accounts } = useCoA();
  const opts = useMemo(
    () => accounts.filter((a) => !a.is_group && (!types || types.includes(a.account_type))),
    [accounts, types]);
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="max-h-64">
        {opts.map((a) => (
          <SelectItem key={a.code} value={a.code}>
            <span className="font-mono-alt text-xs font-semibold">{a.code}</span>
            <span className="text-slate-600"> · {a.name}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Live journal preview — the system decides Dr/Cr sides automatically.
// rows: [{ code, debit?, credit?, label? }]
export function GLPreview({ rows, currency = "USD", note, testid = "gl-preview" }) {
  const { byCode } = useCoA();
  const clean = (rows || []).filter((r) => r && ((r.debit || 0) > 0 || (r.credit || 0) > 0));
  if (!clean.length) return null;
  const dr = clean.reduce((s, r) => s + (r.debit || 0), 0);
  const cr = clean.reduce((s, r) => s + (r.credit || 0), 0);
  const fmt = (n) => currency === "USD"
    ? formatCurrency(n)
    : `${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
  return (
    <div className="rounded-md border border-blue-100 bg-blue-50/50 p-2.5" data-testid={testid}>
      <div className="flex items-center gap-1.5 font-mono-alt text-[10px] font-semibold uppercase tracking-[0.14em] text-[#1E5CBA]">
        <Scales size={11} weight="fill" /> GL posting preview · double-entry (automatic)
      </div>
      <table className="mt-1.5 w-full text-[11px]">
        <thead>
          <tr className="text-left text-[9px] uppercase tracking-wider text-slate-400">
            <th className="pb-0.5 font-semibold">Account</th>
            <th className="pb-0.5 text-right font-semibold">Debit</th>
            <th className="pb-0.5 text-right font-semibold">Credit</th>
          </tr>
        </thead>
        <tbody>
          {clean.map((r, i) => (
            <tr key={i} className="text-slate-700">
              <td className="py-0.5">
                <span className="font-mono-alt font-semibold text-slate-900">{r.code}</span>
                <span className="text-slate-500"> · {byCode[r.code]?.name || r.label || "—"}</span>
              </td>
              <td className="py-0.5 text-right font-mono-alt tabular-nums">{r.debit ? fmt(r.debit) : ""}</td>
              <td className="py-0.5 text-right font-mono-alt tabular-nums">{r.credit ? fmt(r.credit) : ""}</td>
            </tr>
          ))}
          <tr className="border-t border-blue-200/70 font-semibold text-slate-900">
            <td className="pt-1 text-[9px] uppercase tracking-wider text-slate-500">
              Totals{Math.abs(dr - cr) < 0.01 ? " · balanced ✓" : ""}
            </td>
            <td className="pt-1 text-right font-mono-alt tabular-nums">{fmt(dr)}</td>
            <td className="pt-1 text-right font-mono-alt tabular-nums">{fmt(cr)}</td>
          </tr>
        </tbody>
      </table>
      {note && <div className="mt-1 text-[10px] text-slate-500">{note}</div>}
    </div>
  );
}

// Shows every posted GL journal linked to a source record (AR invoice, AP bill…).
// item: { collection, id, label } | null
export function GLEntriesDialog({ item, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!item) { setData(null); return; }
    api.get("/finance/gl/source-journals", { params: { collection: item.collection, source_id: item.id } })
      .then(({ data }) => setData(data))
      .catch(() => setData({ items: [] }));
  }, [item]);
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="gl-entries-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <BookOpenText size={18} className="text-[#1E5CBA]" weight="fill" />
            GL journals · {item?.label || ""}
          </DialogTitle>
          <DialogDescription>Every double-entry journal auto-posted from this record.</DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="font-mono-alt text-xs uppercase tracking-widest text-slate-400">Loading…</div>
        ) : data.items.length === 0 ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800" data-testid="gl-entries-empty">
            No GL journals found for this record — it may predate GL auto-posting, or the posting was
            skipped (check Finance → Tax &amp; Controls → Ops Monitor for failed events).
          </div>
        ) : (
          <div className="space-y-3">
            {data.items.map((e) => (
              <div key={e.id} className="rounded-md border border-slate-200" data-testid={`gl-entry-${e.entry_no}`}>
                <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50/70 px-3 py-2">
                  <span className="font-mono-alt text-xs font-bold text-slate-900">{e.entry_no}</span>
                  <span className="font-mono-alt text-[10px] tabular-nums text-slate-500">{formatDate(e.date)}</span>
                  <span className={`rounded-full px-2 py-0.5 font-mono-alt text-[9px] font-semibold uppercase tracking-wider ${e.status === "posted" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {e.status}{e.is_reversal ? " · reversal" : ""}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-right text-[11px] text-slate-500">{e.memo}</span>
                </div>
                <table className="w-full text-[11px]">
                  <tbody>
                    {(e.lines || []).map((ln) => (
                      <tr key={ln.id} className="border-b border-slate-50 last:border-0">
                        <td className="px-3 py-1">
                          <span className="font-mono-alt font-semibold text-slate-900">{ln.account_code}</span>
                          <span className="text-slate-500"> · {ln.account_name}</span>
                          {ln.description && <div className="text-[10px] text-slate-400">{ln.description}</div>}
                        </td>
                        <td className="px-3 py-1 text-right font-mono-alt tabular-nums">{ln.debit ? formatCurrency(ln.debit) : ""}</td>
                        <td className="px-3 py-1 text-right font-mono-alt tabular-nums">{ln.credit ? formatCurrency(ln.credit) : ""}</td>
                      </tr>
                    ))}
                    <tr className="bg-slate-50/60 font-semibold">
                      <td className="px-3 py-1 text-[9px] uppercase tracking-wider text-slate-500">Totals</td>
                      <td className="px-3 py-1 text-right font-mono-alt tabular-nums">{formatCurrency(e.total_debit)}</td>
                      <td className="px-3 py-1 text-right font-mono-alt tabular-nums">{formatCurrency(e.total_credit)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
